<script>
	import { onMount, onDestroy } from "svelte";
	import { Col, Row } from "@sveltestrap/sveltestrap";
	import PageHeader from "../../../components/PageHeader/PageHeader.svelte";
	import CustomTable from "../../../components/CustomTable/CustomTable.svelte";
	import CustomSelect from "../../../components/CustomSelect/CustomSelect.svelte";
	import CustomDatePicker from "../../../components/CustomDatePicker/CustomDatePicker.svelte";
	import CustomButton from "../../../components/CustomButton/CustomButton.svelte";
	import SearchIcon from "../../../components/SearchIcon/SearchIcon.svelte";
	import { getWorkshopWorkOrders } from "src/services/maintenanceRepair";
	import {
		daysElapsed,
		workshopVisibleWorkOrderStatus,
	} from "src/utils/misc";
	import { workOrderStatusOptions, pageSizeOptions } from "src/utils/options";
	import {
		initPage,
		sizeSelect,
		totalPages,
		totalRecords,
	} from "../../../store/store";

	// PendingCompletion/Completed are shown/filtered as "Completed by
	// Workshop" (see workshopVisibleWorkOrderStatus).
	const statusSelectOptions = workOrderStatusOptions.filter((option) =>
		["InWorkshop", "CompletedByWorkshop", "Cancelled"].includes(
			option.value
		)
	);

	let workOrders = [];

	let plateNumberInput = "";
	let lastUpdatedFrom = "";
	let lastUpdatedTo = "";
	let statusFilter = "";

	// ponytail: fetches one oversized page and filters/sorts/paginates
	// client-side, matching MainMaintenanceRepair's convention. Move to real
	// server-side paging (the endpoint already supports page/pageSize) if a
	// workshop's InWorkshop+ backlog ever outgrows this.
	async function fetchWorkOrders() {
		const res = await getWorkshopWorkOrders({ page: 1, pageSize: 200 });
		workOrders = res.data.items;
	}

	onMount(async () => {
		initPage.set(1);
		sizeSelect.set("10");
		await fetchWorkOrders();
	});

	onDestroy(() => {
		initPage.set(1);
		sizeSelect.set("20");
		totalPages.set(1);
		totalRecords.set(0);
	});

	let appliedFilters = {
		plateNumber: "",
		lastUpdatedFrom: "",
		lastUpdatedTo: "",
		status: "",
	};

	let sortColumn = "";
	let sortDirection = "asc";

	function handleSearch() {
		appliedFilters = {
			plateNumber: plateNumberInput.trim().toUpperCase(),
			lastUpdatedFrom,
			lastUpdatedTo,
			status: statusFilter,
		};
		initPage.set(1);
	}

	function handlePageSizeChange() {
		initPage.set(1);
	}

	function handleSort(event) {
		sortColumn = event.detail.column;
		sortDirection =
			event.detail.direction === "descending" ? "desc" : "asc";
	}

	$: filteredWorkOrders = workOrders
		.filter((workOrder) =>
			appliedFilters.plateNumber
				? workOrder.plateNumber.includes(appliedFilters.plateNumber)
				: true
		)
		.filter((workOrder) => {
			if (!workOrder.updatedAt) return true;
			const updatedDate = workOrder.updatedAt.slice(0, 10);
			if (
				appliedFilters.lastUpdatedFrom &&
				updatedDate < appliedFilters.lastUpdatedFrom
			) {
				return false;
			}
			if (
				appliedFilters.lastUpdatedTo &&
				updatedDate > appliedFilters.lastUpdatedTo
			) {
				return false;
			}
			return true;
		})
		.filter((workOrder) =>
			appliedFilters.status
				? workshopVisibleWorkOrderStatus(workOrder.status) ===
					appliedFilters.status
				: true
		);

	$: sortedWorkOrders = sortColumn
		? [...filteredWorkOrders].sort((a, b) => {
				const aVal = a[sortColumn];
				const bVal = b[sortColumn];
				const result = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
				return sortDirection === "asc" ? result : -result;
			})
		: filteredWorkOrders;

	$: totalRecords.set(sortedWorkOrders.length);
	$: computedTotalPages = Math.max(
		1,
		Math.ceil(sortedWorkOrders.length / Number($sizeSelect))
	);
	$: totalPages.set(computedTotalPages);
	$: if ($initPage > computedTotalPages) initPage.set(computedTotalPages);
	$: pagedWorkOrders = sortedWorkOrders.slice(
		($initPage - 1) * Number($sizeSelect),
		$initPage * Number($sizeSelect)
	);

	function serviceCategoryDisplay(issues) {
		const categories = [
			...new Set(issues.map((issue) => issue.serviceCategory)),
		];
		return categories.join(", ") || "-";
	}

	function toDisplayWorkOrder(workOrder) {
		return {
			id: workOrder.id,
			plateNumber: workOrder.plateNumber,
			makeAndModel: workOrder.makeAndModel,
			status: workshopVisibleWorkOrderStatus(workOrder.status),
			serviceCategory: serviceCategoryDisplay(workOrder.issues),
			noOfIssues: workOrder.issues.length,
			// Only counts while the vehicle is still at the workshop.
			daysInWorkshopDisplay:
				workOrder.status === "InWorkshop" && workOrder.enteredWorkshopAt
					? String(daysElapsed(workOrder.enteredWorkshopAt))
					: "-",
			updatedAt: workOrder.updatedAt,
			// Clicking the plate number goes to the work order detail page.
			// This endpoint has no vehicleId, so the existing
			// /:vehicleId/details/:workOrderId route is reused with the
			// plate number filling the :vehicleId segment.
			detailPath: `${workOrder.plateNumber}/details/${workOrder.id}`,
		};
	}

	$: tableRows = pagedWorkOrders.map(toDisplayWorkOrder);

	const tableColumns = [
		{
			key: "plateNumber",
			label: "Vehicle plate number",
			type: "link",
			linkBy: "detailPath",
			linkTo: "/maintenance-repair/",
		},
		{ key: "makeAndModel", label: "Make and model" },
		{ key: "serviceCategory", label: "Service category" },
		{
			key: "status",
			label: "Work order status",
			type: "status",
			options: "workOrder",
		},
		{ key: "noOfIssues", label: "No. of issues" },
		{ key: "daysInWorkshopDisplay", label: "Days in workshop" },
		{
			key: "updatedAt",
			label: "Last updated",
			type: "date",
			format: "DD MMM YYYY, h:mm A",
			sortCol: "updatedAt",
		},
	];
</script>

<Row>
	<Col xs={12}>
		<Col className="row d-flex align-items-start mx-0">
			<PageHeader title="Maintenance & Repairs" />
		</Col>

		<div class="filters-row mb-3">
			<div class="filters-group">
				<div class="filter-field filter-field-search">
					<label for="wo-plate-number" class="visually-hidden"
						>Search by vehicle plate number</label
					>
					<div class="search-input-wrapper">
						<SearchIcon />
						<input
							id="wo-plate-number"
							type="text"
							class="form-control getgo input mr-search-input"
							placeholder="Search by vehicle plate number"
							bind:value={plateNumberInput}
							on:keydown={(e) =>
								e.key === "Enter" && handleSearch()}
						/>
					</div>
				</div>
				<div class="filter-field">
					<CustomDatePicker
						name="wo-last-updated"
						placeholder="Last updated"
						isRange={true}
						value={lastUpdatedFrom && lastUpdatedTo
							? [lastUpdatedFrom, lastUpdatedTo]
							: ""}
						onChange={(e) => {
							lastUpdatedFrom = e.detail[0];
							lastUpdatedTo = e.detail[1];
						}}
					/>
				</div>
				<div class="filter-field">
					<CustomSelect
						id="wo-status"
						label="Filter by status"
						defaultOption="All statuses"
						bind:value={statusFilter}
						options={statusSelectOptions}
					/>
				</div>
				<CustomButton buttonText="Search" on:click={handleSearch} />
			</div>
			<div class="actions-group">
				<CustomSelect
					id="wo-page-size"
					label="Items per page"
					classes="page-size-select"
					bind:value={$sizeSelect}
					onChange={handlePageSizeChange}
					options={pageSizeOptions}
				/>
			</div>
		</div>

		<CustomTable
			columns={tableColumns}
			rows={tableRows}
			useNavigation
			on:sort={handleSort}
		/>
	</Col>
</Row>

<style>
	.filters-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
	}

	.filters-group {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		flex: 1 1 auto;
	}

	.actions-group {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
		margin-left: auto;
	}

	.filters-group :global(.fds-button.btn-primary) {
		border: 1px solid #d3d3d3;
		background-color: var(--fds-primary, #003bed);
		color: var(--fds-on-primary, #fafbff);
	}

	.filter-field {
		flex: 0 1 200px;
		min-width: 160px;
	}

	.filter-field-search {
		flex-basis: 260px;
	}

	.search-input-wrapper {
		position: relative;
	}

	.search-input-wrapper :global(.form-control.getgo.input.mr-search-input) {
		padding-left: 36px;
	}

	:global(select.page-size-select) {
		width: 120px;
	}
</style>
